
import { useState, useEffect } from "react";
import { Filter, Users, CheckCircle, TrendingUp, ArrowRight } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";
import DashboardLayout from "../../components/DashboardLayout";
import DateRangePicker from "../../components/DateRangePicker";
import { reportsApi } from "@/lib/api";
import { localToday } from "@/lib/data";

function KpiCard({ label, value, sub, icon: Icon, color, bg }: any) {
  return (
    <div className={`${bg} rounded-xl p-4 border border-stone-100`}>
      <div className={`w-9 h-9 rounded-lg ${color} bg-white/80 flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-xs font-medium text-stone-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-stone-800">{value}</p>
      {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
    </div>
  );
}

const FUNNEL_COLORS = ["#6366f1", "#3b82f6", "#10b981", "#f59e0b", "#ef4444"];
const STATUS_COLORS: Record<string, string> = {
  new: "#6366f1",
  follow_up: "#3b82f6",
  converted: "#10b981",
  lost: "#ef4444",
  pending: "#f59e0b",
};

export default function EnquiryFunnelPage() {
  const today = localToday();
  const firstOfYear = today.slice(0, 4) + "-01-01";

  const [fromDate, setFromDate] = useState(firstOfYear);
  const [toDate, setToDate] = useState(today);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await reportsApi.enquiryFunnel({ from_date: fromDate, to_date: toDate });
      setData(res.data?.data);
    } catch { /* empty */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [fromDate, toDate]);

  // Backend: funnel.enquiries / .quotations / .events are objects with .total, .new, etc.
  const funnel     = data?.funnel ?? {};
  const leadSources: any[] = data?.lead_sources ?? [];
  const monthly: any[]     = data?.monthly_trend ?? [];
  const rates = data?.conversion_rates ?? {};

  const totalEnquiries  = funnel.enquiries?.total ?? 0;
  const totalQuotations = funnel.quotations?.total ?? 0;
  const totalEvents     = funnel.events?.total ?? 0;
  const enqToQuote      = rates.enquiry_to_quotation ?? 0;
  const quoteToEvent    = rates.quotation_to_event ?? 0;
  const overallRate     = rates.overall ?? 0;

  const funnelData = [
    { name: "Enquiries", value: totalEnquiries, fill: "#6366f1" },
    { name: "Quotations", value: totalQuotations, fill: "#3b82f6" },
    { name: "Events", value: totalEvents, fill: "#10b981" },
  ];

  // Build status breakdown from funnel object
  const enqObj = funnel.enquiries ?? {};
  const statusChartData = [
    { name: "new",       count: Number(enqObj.new       ?? 0), fill: STATUS_COLORS.new },
    { name: "qualified", count: Number(enqObj.qualified ?? 0), fill: STATUS_COLORS.follow_up },
    { name: "converted", count: Number(enqObj.converted ?? 0), fill: STATUS_COLORS.converted },
    { name: "rejected",  count: Number(enqObj.rejected  ?? 0), fill: STATUS_COLORS.lost },
  ].filter(s => s.count > 0).map(s => ({ ...s, name: s.name.replace(/_/g, " ") }));

  const sourceChartData = leadSources.map((s: any) => ({
    name: (s.source || "Unknown").replace(/_/g, " "),
    count: Number(s.count ?? 0),
  }));

  const monthlyChartData = monthly.map((m: any) => ({
    month: m.month,
    enquiries: Number(m.count ?? 0),
    converted: Number(m.converted ?? 0),
  }));

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-indigo-600" />
            <h1 className="text-lg font-bold text-stone-800">Enquiry Funnel</h1>
          </div>
          <DateRangePicker
            fromDate={fromDate} toDate={toDate}
            onFromChange={setFromDate} onToChange={setToDate}
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48 text-stone-400 text-sm">Loading…</div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard label="Total Enquiries" value={totalEnquiries} sub="in period" icon={Users} color="text-indigo-600" bg="bg-indigo-50" />
              <KpiCard label="Enq → Quotation" value={`${enqToQuote.toFixed(1)}%`} sub={`${totalQuotations} quotations`} icon={ArrowRight} color="text-blue-600" bg="bg-blue-50" />
              <KpiCard label="Quote → Event" value={`${quoteToEvent.toFixed(1)}%`} sub={`${totalEvents} events`} icon={CheckCircle} color="text-emerald-600" bg="bg-emerald-50" />
              <KpiCard label="Overall Rate" value={`${overallRate.toFixed(1)}%`} sub="enquiry to event" icon={TrendingUp} color="text-orange-600" bg="bg-orange-50" />
            </div>

            {/* Funnel Steps visual */}
            <div className="bg-white rounded-xl border border-stone-200 p-4">
              <h2 className="text-sm font-semibold text-stone-700 mb-4">Conversion Funnel</h2>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 py-4">
                {funnelData.map((step, i) => {
                  const pct = i === 0
                    ? 100
                    : funnelData[0].value > 0
                      ? (step.value / funnelData[0].value) * 100
                      : 0;
                  const w = Math.max(20, pct);
                  return (
                    <div key={step.name} className="flex sm:flex-col items-center gap-2">
                      <div
                        className="flex items-center justify-center rounded-lg text-white text-sm font-bold h-14 sm:h-16 transition-all"
                        style={{ backgroundColor: step.fill, width: `${w}%`, minWidth: 90, maxWidth: 240, minHeight: 56 }}
                      >
                        {step.value}
                      </div>
                      <div className="text-center">
                        <div className="text-xs font-medium text-stone-600">{step.name}</div>
                        {i > 0 && (
                          <div className="text-xs text-stone-400">{pct.toFixed(1)}% of enquiries</div>
                        )}
                      </div>
                      {i < funnelData.length - 1 && (
                        <ArrowRight className="hidden sm:block w-4 h-4 text-stone-300 flex-shrink-0" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Status Breakdown */}
              <div className="bg-white rounded-xl border border-stone-200 p-4">
                <h2 className="text-sm font-semibold text-stone-700 mb-4">Enquiries by Status</h2>
                {statusChartData.length === 0 ? (
                  <div className="flex items-center justify-center h-40 text-stone-400 text-sm">No data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={statusChartData} barCategoryGap="35%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f4" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip formatter={(v: any) => [v, "Count"]} />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {statusChartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Source Breakdown */}
              <div className="bg-white rounded-xl border border-stone-200 p-4">
                <h2 className="text-sm font-semibold text-stone-700 mb-4">Enquiries by Source</h2>
                {sourceChartData.length === 0 ? (
                  <div className="flex items-center justify-center h-40 text-stone-400 text-sm">No source data</div>
                ) : (
                  <div className="space-y-2">
                    {sourceChartData.map((s, i) => {
                      const max = Math.max(...sourceChartData.map(x => x.count), 1);
                      const pct = (s.count / max) * 100;
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <div className="text-xs text-stone-600 w-28 truncate capitalize">{s.name}</div>
                          <div className="flex-1 h-5 bg-stone-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full bg-indigo-400 flex items-center justify-end pr-2"
                              style={{ width: `${pct}%`, minWidth: s.count > 0 ? 32 : 0 }}
                            >
                              <span className="text-[10px] text-white font-medium">{s.count}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Monthly Trend */}
            {monthlyChartData.length > 0 && (
              <div className="bg-white rounded-xl border border-stone-200 p-4">
                <h2 className="text-sm font-semibold text-stone-700 mb-4">Monthly Enquiry Trend</h2>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={monthlyChartData} barCategoryGap="30%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f4" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="enquiries" name="Enquiries" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="converted" name="Converted" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
